import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { Member } from '@ui/interfaces'
import { pipelineService, TeamPermission, UserPermission } from '@ui/services/query-rails/pipelineService'
import { Team } from '@ui/services/query-rails/teamService'

type InvitedUser = Member & {
  userPermission: UserPermission
}

type InvitedTeam = Team & {
  teamPermission: TeamPermission
}

export const useManagePipeline = (pipelineId: string, teams: Team[], members: Member[], currentMember: Member) => {
  const pipelineQueryKey = ['pipeline', pipelineId]

  const {
    data: pipeline,
    refetch,
    isLoading: isPipelineLoading,
  } = useQuery({
    queryKey: pipelineQueryKey,
    queryFn: () => pipelineService.getPipeline(pipelineId, currentMember),
  })

  const inviteTeam = useMutation({
    mutationFn: async (teamId: string) => pipelineService.inviteTeam(pipelineId, teamId, currentMember),
    onSuccess: () => refetch(),
  })

  const removeTeam = useMutation({
    mutationFn: async (teamPermissionId: number) => pipelineService.removeTeam(teamPermissionId, currentMember),
    onSuccess: () => refetch(),
  })

  const inviteUser = useMutation({
    mutationFn: async (memberId: number) => pipelineService.inviteUser(pipelineId, memberId, currentMember),
    onSuccess: () => refetch(),
  })

  const removeUser = useMutation({
    mutationFn: async (userPermissionId: number) => pipelineService.removeUser(userPermissionId, currentMember),
    onSuccess: () => refetch(),
  })

  const invitedTeams = useMemo(() => {
    return teams.reduce<InvitedTeam[]>((acc, team) => {
      const teamPermission = pipeline?.team_permissions.find((permission) => permission.team_id === team.id)
      if (!teamPermission) return acc // not an invited team

      const teamWithPermission = {
        ...team,
        teamPermission,
      }

      return [...acc, teamWithPermission]
    }, [])
  }, [pipeline?.team_permissions, teams])

  const invitedUsers = useMemo(() => {
    return members.reduce<InvitedUser[]>((acc, user) => {
      const userPermission = pipeline?.user_permissions.find((permission) => permission.user_id === user.id)
      if (!userPermission) return acc // not an invited user

      const userWithPermission = {
        ...user,
        userPermission,
      }

      return [...acc, userWithPermission]
    }, [])
  }, [pipeline?.user_permissions, members])

  return { invitedTeams, inviteTeam, removeTeam, inviteUser, removeUser, invitedUsers, isPipelineLoading }
}
